import FreeShippingOffer, { FREE_SHIPPING_THRESHOLD } from '../components/FreeShippingOffer';
import '../components/CouponOffers.css';
import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { getTrackingSessionId, trackEvent } from '../utils/tracking';
import './Checkout.css';

const COUNTRIES = [
  { value: 'India',          label: '🇮🇳 India',          currency: 'INR', zone: 'india' },
  { value: 'United States',  label: '🇺🇸 United States',  currency: 'USD', zone: 'usa' },
  { value: 'United Kingdom', label: '🇬🇧 United Kingdom', currency: 'GBP', zone: 'uk' },
  { value: 'Singapore',      label: '🇸🇬 Singapore',      currency: 'SGD', zone: 'singapore' },
  { value: 'Australia',      label: '🇦🇺 Australia',      currency: 'AUD', zone: 'australia' },
  { value: 'Malaysia',       label: '🇲🇾 Malaysia',       currency: 'MYR', zone: 'malaysia' },
];

// State / province lists for countries where we have a known set. Countries
// not listed here (e.g. Singapore) fall back to a free-text input.
const STATES = {
  India: [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
    'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
    'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
  ],
  'United States': [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming', 'Washington, D.C.',
  ],
  'United Kingdom': ['England', 'Scotland', 'Wales', 'Northern Ireland'],
  Australia: [
    'Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland',
    'South Australia', 'Tasmania', 'Victoria', 'Western Australia',
  ],
  Malaysia: [
    'Johor', 'Kedah', 'Kelantan', 'Kuala Lumpur', 'Labuan', 'Malacca', 'Negeri Sembilan',
    'Pahang', 'Penang', 'Perak', 'Perlis', 'Putrajaya', 'Sabah', 'Sarawak', 'Selangor', 'Terengganu',
  ],
};

// 2-letter country codes for the zippopotam.us postal lookup (non-India).
const POSTAL_COUNTRY_CODE = {
  India: 'in', 'United States': 'us', 'United Kingdom': 'gb',
  Singapore: 'sg', Australia: 'au', Malaysia: 'my',
};

// Resolve { city, state } from a postal code. India uses the official India Post
// API (best coverage); other countries use zippopotam.us. Returns null on miss.
async function lookupPincodeLocation(pincode, country) {
  const pin = String(pincode || '').trim();
  try {
    if (country === 'India') {
      if (!/^\d{6}$/.test(pin)) return null;
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await res.json();
      const po = data?.[0]?.Status === 'Success' ? data[0].PostOffice?.[0] : null;
      return po ? { city: po.District || '', state: po.State || '' } : null;
    }
    const cc = POSTAL_COUNTRY_CODE[country];
    if (!cc || pin.length < 3) return null;
    const res = await fetch(`https://api.zippopotam.us/${cc}/${encodeURIComponent(pin)}`);
    if (!res.ok) return null;
    const data = await res.json();
    const place = data?.places?.[0];
    return place ? { city: place['place name'] || '', state: place['state'] || '' } : null;
  } catch {
    return null;
  }
}


const Checkout = () => {
  const { items, subtotal, clearCart, updateQuantity, appliedCoupon, couponLoading, setSelectedCouponCode } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [processing, setProcessing] = useState(false);
  // The basket is folded in the summary — the totals are what this panel is for.
  const [itemsOpen, setItemsOpen] = useState(false);
  const [offers, setOffers] = useState([]);           // every live coupon, for the list below the box

  const [address, setAddress] = useState({
    fullName: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India'
  });

  // Billing address — defaults to "same as delivery". When the customer opts
  // for a separate billing address, these fields are captured and sent.
  const [billingSameAsDelivery, setBillingSameAsDelivery] = useState(true);
  const [billing, setBilling] = useState({
    fullName: '',
    phone: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India'
  });
  const handleBillingChange = (e) =>
    setBilling((b) => ({ ...b, [e.target.name]: e.target.value }));

  // Prefill the delivery address from the customer's saved (default) address so
  // they don't re-enter it every time. Only fills blank fields the user hasn't
  // already typed.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get('/auth/me')
      .then((res) => {
        if (cancelled) return;
        const list = res.data.user?.addresses || [];
        const saved = list.find((a) => a.isDefault) || list[0];
        if (!saved) return;
        setAddress((prev) => ({
          ...prev,
          fullName: prev.fullName || saved.fullName || '',
          phone: prev.phone || saved.phone || '',
          line1: prev.line1 || saved.line1 || '',
          line2: prev.line2 || saved.line2 || '',
          city: prev.city || saved.city || '',
          state: prev.state || saved.state || '',
          pincode: prev.pincode || saved.pincode || '',
          country: saved.country || prev.country,
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  // Enabled payment methods (admin-controlled). Defaults to online until loaded.
  const [enabledMethods, setEnabledMethods] = useState({ razorpay: true, cod: false, upi: false });
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [upiPayment, setUpiPayment] = useState(null); // { order, upiUrl, qrCodeUrl, vpa, payeeName, amount, reference }
  const [upiTxnRef, setUpiTxnRef] = useState('');
  const [upiPayerVpa, setUpiPayerVpa] = useState('');
  const [upiClaiming, setUpiClaiming] = useState(false);

  // Address search (Nominatim / OpenStreetMap)
  const [addrQuery, setAddrQuery] = useState('');
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrSearchLoading, setAddrSearchLoading] = useState(false);
  const [addrDropdownOpen, setAddrDropdownOpen] = useState(false);

  // Live rate state
  const [liveRates, setLiveRates] = useState(null);     // null = not yet fetched, [] = API failed
  const [liveRatesLoading, setLiveRatesLoading] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const countryConfig = COUNTRIES.find(c => c.value === address.country) || COUNTRIES[0];
  const isIndia = countryConfig.zone === 'india';
  const isFreeShipping = isIndia && subtotal >= FREE_SHIPPING_THRESHOLD;

  // Estimated delivery time: 24 hrs in Hyderabad, 1–2 days rest of India, 3–7 days international
  const isHyderabad = isIndia && (
    /hyderabad|secunderabad/i.test(address.city || '') ||
    /^(500|501|502|503)/.test(String(address.pincode || '').trim())
  );
  const deliveryEstimate = !isIndia
    ? '3–7 business days'
    : isHyderabad ? 'within 24 hours' : '1–2 business days';

  // shippingCost is only known once a service is selected
  const shippingConfirmed = selectedService !== null || isFreeShipping;
  // The quoted rate behind a waived charge, so the summary can strike it out.
  const waivedRate = isFreeShipping ? Number(liveRates?.[0]?.total) || 0 : 0;
  const shippingCost = !shippingConfirmed ? null
    : isFreeShipping ? 0
    : selectedService.total;
  const discountAmount = appliedCoupon?.discount || 0;
  const total = subtotal - discountAmount + (shippingCost ?? 0);

  useEffect(() => {
    trackEvent('begin_checkout', {
      cartValue: subtotal,
      cartItems: items,
      metadata: { source: 'checkout_page', subtotal, shippingStatus: 'pending_address' },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Every live coupon, so the page can show what is on offer instead of asking
  // people to already know a code. /coupons/active hides expired ones and ones
  // that have hit their usage limit, and returns only the fields needed to work
  // out whether this basket qualifies.
  useEffect(() => {
    api.get('/coupons/active')
      .then(r => setOffers(r.data.coupons || []))
      .catch(() => setOffers([]));
  }, []);

  // Only ever called with a code from the offers list now that the type-it-in
  // box is gone, so there is no free-text state left to hold.
  const applyCoupon = (code) => {
    const clean = String(code || '').trim().toUpperCase();
    if (clean) setSelectedCouponCode(clean);
  };

  // Recalculate rates after address or quantity changes; discard stale responses.
  useEffect(() => {
    let cancelled = false;
    setLiveRates(null);
    setSelectedService(null);
    const minLength = isIndia ? 6 : 3;
    if (!address.pincode || address.pincode.length < minLength) {
      setLiveRatesLoading(false);
      return;
    }
    setLiveRatesLoading(true);
    const timer = setTimeout(() => {
      api.post('/shipping/rate', {
        pincode: address.pincode,
        country: address.country,
        items: items.map(i => ({ productId: i.productId, weight: i.weight, quantity: i.quantity })),
      }).then(r => {
        if (cancelled) return;
        const services = r.data.services || [];
        setLiveRates(services);
        setSelectedService(services[0] || null);
      }).catch(() => { if (!cancelled) setLiveRates([]); })
        .finally(() => { if (!cancelled) setLiveRatesLoading(false); });
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [address.pincode, address.country, isIndia, items]);

  // Auto-fill delivery city + state from the postal code (debounced)
  useEffect(() => {
    const minLength = address.country === 'India' ? 6 : 3;
    if (!address.pincode || address.pincode.trim().length < minLength) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const loc = await lookupPincodeLocation(address.pincode, address.country);
      if (cancelled || !loc) return;
      setAddress(prev => ({
        ...prev,
        city: loc.city || prev.city,
        state: loc.state || prev.state,
      }));
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [address.pincode, address.country]);

  // Load which payment methods the admin has enabled
  useEffect(() => {
    api.get('/settings/payment-methods')
      .then(res => {
        const methods = res.data.methods || {};
        setEnabledMethods(methods);
        const firstEnabled = ['icici', 'razorpay', 'cod', 'upi'].find(k => methods[k]);
        if (firstEnabled) setPaymentMethod(firstEnabled);
      })
      .catch(() => {});
  }, []);

  // Auto-fill billing city + state from the postal code (debounced)
  useEffect(() => {
    if (billingSameAsDelivery) return;
    const minLength = billing.country === 'India' ? 6 : 3;
    if (!billing.pincode || billing.pincode.trim().length < minLength) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const loc = await lookupPincodeLocation(billing.pincode, billing.country);
      if (cancelled || !loc) return;
      setBilling(prev => ({
        ...prev,
        city: loc.city || prev.city,
        state: loc.state || prev.state,
      }));
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [billing.pincode, billing.country, billingSameAsDelivery]);

  // Load Razorpay checkout script once
  useEffect(() => {
    if (window.Razorpay) return;
    if (document.getElementById('razorpay-checkout-js')) return;
    const s = document.createElement('script');
    s.id = 'razorpay-checkout-js';
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const handleAddressChange = e => {
    setAddress(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Nominatim address search
  useEffect(() => {
    if (addrQuery.length < 4) { setAddrSuggestions([]); return; }
    const timer = setTimeout(() => {
      setAddrSearchLoading(true);
      fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(addrQuery)}&format=json&addressdetails=1&limit=6&accept-language=en`)
        .then(r => r.json())
        .then(data => { setAddrSuggestions(data); setAddrDropdownOpen(true); })
        .catch(() => setAddrSuggestions([]))
        .finally(() => setAddrSearchLoading(false));
    }, 500);
    return () => clearTimeout(timer);
  }, [addrQuery]);

  const applyAddrSuggestion = (place) => {
    const a = place.address || {};
    const countryName = a.country || '';
    const matched = COUNTRIES.find(c => c.value.toLowerCase() === countryName.toLowerCase());
    const road = [a.house_number, a.road].filter(Boolean).join(' ');
    const line2 = [a.suburb, a.neighbourhood, a.quarter].filter(Boolean)[0] || '';
    const city = a.city || a.town || a.village || a.municipality || '';
    const state = a.state || a.county || '';
    const pincode = a.postcode || '';
    setAddress(prev => ({
      ...prev,
      ...(road && { line1: road }),
      ...(line2 && { line2 }),
      ...(city && { city }),
      ...(state && { state }),
      ...(pincode && { pincode }),
      ...(matched && { country: matched.value }),
    }));
    setAddrQuery('');
    setAddrSuggestions([]);
    setAddrDropdownOpen(false);
  };

  const validateAddress = () => {
    // Phone + a complete address are mandatory; email is optional (guest checkout).
    const required = ['fullName', 'phone', 'line1', 'city', 'country'];
    if (isIndia) required.push('pincode', 'state');
    for (const field of required) {
      if (!address[field]?.trim()) {
        toast.error(`Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }
    // Phone must contain enough digits to be dialable.
    if ((address.phone.replace(/\D/g, '').length) < 8) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    // Email is optional, but if given it must look valid (order updates go there).
    if (address.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.email.trim())) {
      toast.error('Please enter a valid email, or leave it blank');
      return false;
    }
    // When a separate billing address is chosen, validate its core fields too.
    if (!billingSameAsDelivery) {
      const billingRequired = ['fullName', 'line1', 'city', 'country'];
      if (billing.country === 'India') billingRequired.push('pincode', 'state');
      for (const field of billingRequired) {
        if (!billing[field]?.trim()) {
          toast.error(`Please fill in billing ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
          return false;
        }
      }
    }
    return true;
  };

  const goToReview = () => {
    if (!validateAddress()) return;
    if (liveRatesLoading) { toast.error('Please wait — checking shipping rates'); return; }
    if (!isFreeShipping && !selectedService) {
      toast.error('Could not get a shipping rate for this pincode. Please recheck it.');
      return;
    }
    // Persist the delivery address to the customer's account so it is remembered
    // for next time (fire-and-forget — never blocks the checkout).
    if (user) {
      api.put('/auth/checkout-address', {
        fullName: address.fullName,
        phone: address.phone,
        line1: address.line1,
        line2: address.line2,
        city: address.city,
        state: address.state,
        pincode: address.pincode,
        country: address.country,
      }).catch(() => {});
    }
    trackEvent('address_submitted', {
      cartValue: total,
      cartItems: items,
      contact: {
        name: address.fullName,
        email: address.email,
        phone: address.phone,
        country: address.country,
        state: address.state,
        city: address.city,
      },
      metadata: {
        source: 'checkout_review',
        country: address.country,
        state: address.state,
        city: address.city,
        subtotal,
        shippingCost: shippingCost ?? 0,
        discount: discountAmount,
        total,
      },
    });
    setStep(1);
  };

  const placeOrder = async () => {
    setProcessing(true);
    try {
      // 1. Create order
      const orderRes = await api.post('/orders', {
        items: items.map(i => ({
          productId: i.productId,
          weight: i.weight,
          quantity: i.quantity,
          bundleId: i.bundleId,
          bundleType: i.bundleType,
          bundleLabel: i.bundleLabel,
          // Lets the server re-derive the combo price instead of trusting ours.
          comboId: i.comboId,
          customization: i.customization,
        })),
        shippingAddress: address,
        // null when "same as delivery" — backend falls back to shipping for billing
        billingAddress: billingSameAsDelivery ? null : billing,
        shippingCost: shippingCost ?? 0,
        shippingMethod: 'Avakaaya.com Delivery',
        subtotal,
        total: subtotal - discountAmount + (shippingCost ?? 0),
        currency: 'INR',
        paymentMethod,
        couponCode: appliedCoupon?.code,
        discount: discountAmount,
        guestEmail: !user ? address.email : undefined,
        leadSessionId: getTrackingSessionId()
      });

      const order = orderRes.data.order;
      const orderTotal = Number(order.total) || total;
      const orderTrackingPayload = {
        orderId: order._id,
        cartValue: orderTotal,
        cartItems: items,
        contact: { name: address.fullName, email: address.email, phone: address.phone },
        metadata: {
          source: 'order_completion',
          subtotal,
          shippingCost: shippingCost ?? 0,
          discount: discountAmount,
          total: orderTotal,
        },
      };

      if (paymentMethod === 'cod') {
        trackEvent('order_completed', orderTrackingPayload);
        clearCart();
        navigate(`/order/success?orderId=${order._id}&orderNumber=${order.orderNumber}`);
        return;
      }

      if (paymentMethod === 'upi') {
        const upiRes = await api.post('/payment/upi/initiate', {
          orderId: order._id,
          amount: Number(order.total),
        });
        setUpiPayment({ order, ...upiRes.data });
        return;
      }

      // ICICI Bank PG: server starts the sale, we redirect the browser to ICICI.
      // The bank posts the result back to our returnURL, which returns the user
      // to /order/success or /checkout/failed.
      if (paymentMethod === 'icici') {
        const iciciRes = await api.post('/payment/icici/initiate', { orderId: order._id });
        if (iciciRes.data.mock) {
          await api.post('/payment/verify', {
            razorpay_order_id: 'order_mock_' + Date.now(), razorpay_payment_id: 'mock_pay_' + Date.now(),
            razorpay_signature: 'mock_sig', orderId: order._id,
          });
          clearCart();
          navigate(`/order/success?orderId=${order._id}&orderNumber=${order.orderNumber}`);
          return;
        }
        if (!iciciRes.data.paymentUrl) throw new Error('Could not start ICICI payment. Please try again.');
        // Cart clears on the success page after the bank confirms payment.
        window.location.href = iciciRes.data.paymentUrl;
        return;
      }

      // 2. Create Razorpay order
      const payRes = await api.post('/payment/create-order', {
        orderId: order._id,
        amount: Number(order.total),
        currency: 'INR'
      });

      if (payRes.data.mock) {
        // Dev mode: simulate payment
        await api.post('/payment/verify', {
          razorpay_order_id: payRes.data.order.id,
          razorpay_payment_id: 'mock_pay_' + Date.now(),
          razorpay_signature: 'mock_sig',
          orderId: order._id
        });
        trackEvent('order_completed', orderTrackingPayload);
        clearCart();
        navigate(`/order/success?orderId=${order._id}&orderNumber=${order.orderNumber}`);
        return;
      }

      // 3. Open Razorpay payment dialog (real mode)
      const Razorpay = window.Razorpay;
      if (!Razorpay) {
        toast.error('Payment gateway not loaded. Please refresh.');
        return;
      }

      // Razorpay callback URL — used as a fallback if the JS handler fails
      // (popup blocker, iframe restrictions, network issue). The backend route
      // verifies the signature and redirects the user back to /order/success.
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const callbackUrl = apiBase.startsWith('http')
        ? `${apiBase.replace(/\/$/, '')}/payment/callback`
        : `${window.location.origin}${apiBase.replace(/\/$/, '')}/payment/callback`;

      const rzp = new Razorpay({
        key: payRes.data.keyId,
        amount: payRes.data.order.amount,
        currency: 'INR',
        name: 'Avakaaya Foods',
        description: `Order #${order.orderNumber}`,
        order_id: payRes.data.order.id,
        callback_url: callbackUrl,
        prefill: {
          name: address.fullName,
          email: address.email,
          contact: address.phone
        },
        theme: { color: '#1a2e1a' },
        handler: async (response) => {
          await api.post('/payment/verify', {
            ...response,
            orderId: order._id
          });
          trackEvent('order_completed', orderTrackingPayload);
          clearCart();
          navigate(`/order/success?orderId=${order._id}&orderNumber=${order.orderNumber}`);
        },
        modal: {
          ondismiss: () => {
            toast.error('Payment cancelled. Your order is saved — you can retry.');
          }
        }
      });

      rzp.on('payment.failed', (response) => {
        const reason = response?.error?.description || response?.error?.reason || 'Payment failed';
        toast.error(`Payment failed: ${reason}`);
        // Track checkout failure
        trackEvent('checkout_failed', {
          cartValue: subtotal,
          cartItems: items,
          contact: { name: address.fullName, email: address.email, phone: address.phone },
          metadata: {
            source: 'razorpay_failed',
            reason: reason.slice(0, 100),
            subtotal,
            shippingCost: shippingCost ?? 0,
            discount: discountAmount,
            total,
          },
        });
      });

      rzp.open();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Something went wrong. Please try again.';
      toast.error(errorMsg);
      // Track checkout failure
      trackEvent('checkout_failed', {
        cartValue: subtotal,
        cartItems: items,
        contact: { name: address.fullName, email: address.email, phone: address.phone },
        metadata: {
          source: 'checkout_error',
          reason: errorMsg.slice(0, 100),
          subtotal,
          shippingCost: shippingCost ?? 0,
          discount: discountAmount,
          total,
        },
      });
    } finally {
      setProcessing(false);
    }
  };

  const claimUpiPayment = async () => {
    if (!upiTxnRef.trim()) {
      toast.error('Enter the UPI transaction reference shown in your UPI app');
      return;
    }
    setUpiClaiming(true);
    try {
      await api.post('/payment/upi/claim', {
        orderId: upiPayment.order._id,
        upiTxnRef: upiTxnRef.trim(),
        payerVpa: upiPayerVpa.trim() || undefined,
      });
      clearCart();
      toast.success('Thanks! We\'ll verify your payment and confirm the order shortly.');
      navigate(`/order/success?orderId=${upiPayment.order._id}&orderNumber=${upiPayment.order.orderNumber}&pendingVerification=1`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not record payment claim. Try again.');
    } finally {
      setUpiClaiming(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };

  // /cart itself rejects empty carts now, so send them straight to the catalogue.
  if (items.length === 0) return <Navigate to="/products" replace />;

  return (
    <div className="checkout-page">

      <div className="container">
        <h1 className="checkout-title">Checkout</h1>

        {/* No step bar: there are two steps, each headed by its own card title,
            and the strip cost more vertical room than it explained. */}

        <div className="checkout-layout">
          {/* Left: Form */}
          <div className="checkout-form-area">

            {/* Step 0: Address */}
            {step === 0 && (
              <div className="checkout-card">
                {/* Country sits in the heading: it is set once, usually never
                    changed, and it was costing a whole field row. */}
                <div className="checkout-card-head">
                  <h2 className="checkout-card-title">Delivery Address</h2>
                  <div className="addr-search-wrap">
                    <div className="addr-search-input-row">
                      <span className="addr-search-icon">🔍</span>
                      <input
                        className="addr-search-input"
                        type="text"
                        placeholder="Search address to auto-fill…"
                        value={addrQuery}
                        onChange={e => setAddrQuery(e.target.value)}
                        onFocus={() => addrSuggestions.length > 0 && setAddrDropdownOpen(true)}
                        onBlur={() => setTimeout(() => setAddrDropdownOpen(false), 180)}
                        autoComplete="off"
                      />
                      {addrSearchLoading && <span className="shipping-spinner addr-search-spinner" />}
                    </div>
                    {addrDropdownOpen && addrSuggestions.length > 0 && (
                      <ul className="addr-suggestions">
                        {addrSuggestions.map((p, i) => (
                          <li key={i} onMouseDown={() => applyAddrSuggestion(p)} className="addr-suggestion-item">
                            <span className="addr-suggestion-icon">📍</span>
                            <span>{p.display_name}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <select name="country" value={address.country} onChange={handleAddressChange} className="form-select checkout-country-select" aria-label="Delivery country">
                    {COUNTRIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>

                {!user && (
                  <p className="checkout-guest-note">
                    Checking out as guest — no account needed.{' '}
                    <button type="button" className="checkout-guest-login" onClick={() => navigate('/login', { state: { from: { pathname: '/checkout' } } })}>
                      Log in
                    </button>{' '}for faster checkout &amp; order history.
                  </p>
                )}


                {/* Two-up wherever a field doesn't need the width: the column is
                    narrower now, and six stacked rows pushed the button off screen.
                    Line 2 is optional, so it stays folded away until asked for. */}
                <div className="form-row form-row--three">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input name="fullName" value={address.fullName} onChange={handleAddressChange} className="form-input" placeholder="As on delivery ID" />
                  </div>
                  <div className="form-group">
                    <label>Phone / WhatsApp *</label>
                    <input name="phone" value={address.phone} onChange={handleAddressChange} className="form-input" placeholder="+91…" />
                  </div>
                  <div className="form-group">
                    <label>Email (optional)</label>
                    <input name="email" type="email" value={address.email} onChange={handleAddressChange} className="form-input" placeholder="Order updates" />
                  </div>
                </div>
                {/* Both address lines on one row — the fold saved a row but cost
                    a click, and the second line is short enough to sit beside
                    the first rather than under it. */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Address Line 1 *</label>
                    <input name="line1" value={address.line1} onChange={handleAddressChange} className="form-input" placeholder="House/Flat No., Street" />
                  </div>
                  <div className="form-group">
                    <label>Apartment, area, landmark</label>
                    <input name="line2" value={address.line2} onChange={handleAddressChange} className="form-input" placeholder="Optional" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>City *</label>
                    <input name="city" value={address.city} onChange={handleAddressChange} className="form-input" />
                  </div>
                  <div className="form-group">
                    <label>State / Province{isIndia ? ' *' : ''}</label>
                    {STATES[address.country] ? (
                      <select name="state" value={address.state} onChange={handleAddressChange} className="form-select">
                        <option value="">Select state…</option>
                        {STATES[address.country].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <input name="state" value={address.state} onChange={handleAddressChange} className="form-input" />
                    )}
                  </div>
                  <div className="form-group">
                    <label>{isIndia ? 'PIN Code *' : 'ZIP / Postal Code'}</label>
                    <input name="pincode" value={address.pincode} onChange={handleAddressChange} className="form-input" />
                  </div>
                </div>

                {/* Billing address */}
                <label className="billing-same-toggle">
                  <input
                    type="checkbox"
                    checked={billingSameAsDelivery}
                    onChange={e => setBillingSameAsDelivery(e.target.checked)}
                  />
                  <span>Billing address same as delivery address</span>
                </label>

                {!billingSameAsDelivery && (
                  <div className="billing-fields">
                    {/* Same shape as the delivery block above: country on the
                        heading line, then contact, then the two address lines. */}
                    <div className="checkout-card-head">
                      <h3 className="billing-fields-title">Billing Address</h3>
                      <select name="country" value={billing.country} onChange={handleBillingChange} className="form-select checkout-country-select" aria-label="Billing country">
                        {COUNTRIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Full Name *</label>
                        <input name="fullName" value={billing.fullName} onChange={handleBillingChange} className="form-input" placeholder="Name on the bill" />
                      </div>
                      <div className="form-group">
                        <label>Phone</label>
                        <input name="phone" value={billing.phone} onChange={handleBillingChange} className="form-input" placeholder="Optional" />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Address Line 1 *</label>
                        <input name="line1" value={billing.line1} onChange={handleBillingChange} className="form-input" placeholder="House/Flat No., Street" />
                      </div>
                      <div className="form-group">
                        <label>Apartment, area, landmark</label>
                        <input name="line2" value={billing.line2} onChange={handleBillingChange} className="form-input" placeholder="Optional" />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>City *</label>
                        <input name="city" value={billing.city} onChange={handleBillingChange} className="form-input" />
                      </div>
                      <div className="form-group">
                        <label>State / Province{billing.country === 'India' ? ' *' : ''}</label>
                        {STATES[billing.country] ? (
                          <select name="state" value={billing.state} onChange={handleBillingChange} className="form-select">
                            <option value="">Select state…</option>
                            {STATES[billing.country].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : (
                          <input name="state" value={billing.state} onChange={handleBillingChange} className="form-input" />
                        )}
                      </div>
                      <div className="form-group">
                        <label>{billing.country === 'India' ? 'PIN Code *' : 'ZIP / Postal Code'}</label>
                        <input name="pincode" value={billing.pincode} onChange={handleBillingChange} className="form-input" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Back sits beside the button that moves you on, the way it
                    already does on the review step — not orphaned at the top
                    of the page where it read as "leave checkout". */}
                <div className="checkout-step-btns">
                  <button
                    type="button"
                    className="btn btn-outline btn-lg"
                    disabled={processing}
                    onClick={() => navigate('/cart')}
                  >
                    <span aria-hidden="true">←</span> Back to cart
                  </button>
                  <button
                    className="btn btn-primary btn-lg checkout-next-btn"
                    onClick={goToReview}
                  >
                    {liveRatesLoading ? 'Checking shipping…' : 'Review Order →'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 1: Review */}
            {step === 1 && (
              <div className="checkout-card">
                <h2 className="checkout-card-title">Review & Confirm</h2>

                <div className="review-section">
                  <div className="review-row">
                    <span className="review-key">Delivering to</span>
                    <span className="review-val">
                      {address.fullName}, {address.line1}, {address.city}, {address.country} {address.pincode}
                    </span>
                    <button className="review-edit" onClick={() => setStep(0)}>Edit</button>
                  </div>
                  <div className="review-row">
                    <span className="review-key">Shipping</span>
                    <span className="review-val">
                      Avakaaya.com Delivery · {deliveryEstimate}
                      {shippingCost === 0 && shippingConfirmed && <span style={{ color: '#2e7d32', marginLeft: 8 }}>FREE</span>}
                    </span>
                    <button className="review-edit" onClick={() => setStep(0)}>Edit</button>
                  </div>
                  <div className="review-row review-row--payment">
                    <span className="review-key">Payment</span>
                    <div className="pay-method-options">
                      {[
                        ['icici', '🏦 Pay Online (ICICI Bank) - Incl International Cards', 'UPI · Card · Netbanking · Wallets'],
                        ['razorpay', '💳 Pay Online (Razorpay)', 'UPI · Card · Netbanking · Wallets'],
                        ['cod', '💵 Cash on Delivery', 'Pay when your order arrives'],
                        ['upi', '📲 UPI (direct)', 'Pay to our UPI & confirm'],
                      ]
                        .filter(([k]) => enabledMethods[k])
                        .map(([k, label, desc]) => (
                        <label key={k} className={`pay-method${paymentMethod === k ? ' selected' : ''}`}>
                          <input
                            type="radio"
                            name="paymentMethod"
                            checked={paymentMethod === k}
                            onChange={() => setPaymentMethod(k)}
                          />
                          <span className="pay-method-text">
                            <strong>{label}</strong>
                            <span>{desc}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {!isIndia && (
                  <div className="customs-note">
                    <strong>📦 International Shipping Note:</strong> All pickles are packed in compliance with food safety and customs regulations. Delivery times may vary due to customs clearance. Import duties (if any) are the responsibility of the recipient.
                  </div>
                )}

                {/* The basket is listed in the summary, so it is not repeated here.
                    Nor is the Razorpay reassurance — the gateway says so itself on
                    the screen where the card details are actually typed. Cash on
                    delivery still gets a line, because nothing else explains it. */}
                {paymentMethod === 'cod' && (
                  <div className="payment-security-note">
                    💵 Pay in cash when your order is delivered.
                  </div>
                )}

                <div className="checkout-step-btns">
                  <button className="btn btn-outline btn-lg" onClick={() => setStep(0)}>← Back</button>
                  <button
                    className="btn btn-gold btn-lg"
                    onClick={placeOrder}
                    disabled={processing || couponLoading || liveRatesLoading}
                  >
                    {processing ? '⏳ Processing...' : `Place Order · ₹${total.toLocaleString()}`}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right: Order summary */}
          <div className="checkout-summary cart-summary">
            <h2 className="summary-title">Order Summary</h2>
            {/* The basket, folded away. A summary that opens with a list of
                everything you already chose buries the numbers people came
                here to check. */}
            <button
              type="button"
              className="summary-items-toggle"
              aria-expanded={itemsOpen}
              onClick={() => setItemsOpen(o => !o)}
            >
              <span>{items.length} {items.length === 1 ? 'item' : 'items'} in this order</span>
              <span className="summary-items-chevron" aria-hidden="true">{itemsOpen ? '▲' : '▼'}</span>
            </button>
            {itemsOpen && (
              <div className="summary-items-list">
              {items.map(item => (
                <div key={`${item.productId}_${item.weight}_${item.bundleId || 'regular'}`} className="summary-item">
                  <img src={item.thumbnail} alt={item.name} className="summary-item-img" />
                  <div className="summary-item-info">
                    <span className="summary-item-name">{item.name}</span>
                    <span className="summary-item-meta">{item.bundleId ? `${item.bundleLabel || 'Custom hamper'} | ` : ''}{item.weight} x {item.quantity}</span>
                    {!item.bundleId && <div className="checkout-quantity" role="group" aria-label={`Quantity for ${item.name}`}>
                      <button type="button" disabled={processing || item.quantity <= 1} aria-label={`Decrease quantity of ${item.name}`} onClick={() => updateQuantity(item.productId, item.weight, item.quantity - 1)}>-</button>
                      <span aria-live="polite">{item.quantity}</span>
                      <button type="button" disabled={processing} aria-label={`Increase quantity of ${item.name}`} onClick={() => updateQuantity(item.productId, item.weight, item.quantity + 1)}>+</button>
                    </div>}
  
                  </div>
                  <span className="summary-item-price">INR {(Number(item.price) * item.quantity).toLocaleString()}</span>
                </div>
              ))}
              </div>
            )}
            {/* Coupon code */}
            <div className="coupon-section">
              {/* No code box: every live coupon is listed below with what it
                  saves on this basket, so there is nothing left to type. The
                  applied one still shows, and can still be taken off. */}
              {appliedCoupon && (
                <div className="coupon-applied">
                  <span>🎟️ {appliedCoupon.code} applied</span>
                  <button className="coupon-remove" onClick={() => setSelectedCouponCode('')}>✕</button>
                </div>
              )}
              {/* What else is on offer, and whether this basket qualifies. The
                  list stays visible with a coupon applied so a better one can be
                  seen and swapped to — the discount each would give is worked out
                  against the current subtotal, not guessed at. */}
              {offers.length > 0 && (
                <ul className="coupon-offers">
                  {offers.map(o => {
                    const min = Number(o.minOrder) || 0;
                    const cap = Number(o.maxDiscount) || 0;
                    const short = Math.max(0, min - subtotal);
                    const eligible = short === 0;
                    const raw = o.type === 'percent' ? (subtotal * Number(o.value)) / 100 : Number(o.value);
                    const saving = Math.round(Math.min(raw, cap > 0 ? cap : raw));
                    const isOn = appliedCoupon?.code === o.code;
                    return (
                      <li key={o.code} className={`coupon-offer${eligible ? '' : ' is-locked'}${isOn ? ' is-on' : ''}`}>
                        <div className="coupon-offer-main">
                          <span className="coupon-offer-code">{o.code}</span>
                          <span className="coupon-offer-desc">
                            {o.type === 'percent'
                              ? `${Number(o.value)}% off${cap > 0 ? ` up to ₹${cap.toLocaleString('en-IN')}` : ''}`
                              : `₹${Number(o.value).toLocaleString('en-IN')} off`}
                            {min > 0 ? ` · on orders above ₹${min.toLocaleString('en-IN')}` : ''}
                          </span>
                          <span className="coupon-offer-note">
                            {eligible
                              ? `You save ₹${saving.toLocaleString('en-IN')}`
                              : `Add ₹${short.toLocaleString('en-IN')} more to use this`}
                          </span>
                        </div>
                        {isOn ? (
                          <span className="coupon-offer-on">Applied</span>
                        ) : (
                          <button
                            type="button"
                            className="coupon-offer-apply"
                            disabled={!eligible || couponLoading}
                            onClick={() => applyCoupon(o.code)}
                          >
                            {eligible ? 'Apply' : 'Locked'}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="summary-divider" />
            <div className="summary-row">
              <span>Subtotal</span><span>₹{subtotal.toLocaleString()}</span>
            </div>
            <FreeShippingOffer subtotal={subtotal} isIndia={isIndia} />
            {discountAmount > 0 && (
              <div className="summary-row summary-row--green">
                <span>Discount ({appliedCoupon?.code})</span>
                <span>-₹{discountAmount.toLocaleString()}</span>
              </div>
            )}
            <div className="summary-row">
              <span>Shipping</span>
              <span>
                {/* While the courier is being asked, the price slot holds a
                    small spinner — the row keeps its place instead of a
                    banner appearing and pushing the total down. */}
                {liveRatesLoading
                  ? <span className="shipping-spinner shipping-spinner--sm" role="status" aria-label="Checking shipping rates" />
                  : shippingCost === null ? '—'
                    : shippingCost === 0 ? (
                      <>
                        {/* What the courier would have charged, struck through —
                            the saving shown as a price rather than a sentence. */}
                        {waivedRate > 0 && <s className="summary-strike">₹{waivedRate.toLocaleString('en-IN')}</s>}
                        <span className="summary-free">₹0</span>
                      </>
                    )
                      : `₹${shippingCost.toLocaleString()}`}
              </span>
            </div>

            <div className="summary-total">
              <strong>Total</strong>
              <strong>{shippingCost === null ? '—' : `₹${total.toLocaleString()}`}</strong>
            </div>

            {/* Under the total: it is a note about the delivery, not a line of the bill. */}
            {!liveRatesLoading && liveRates && liveRates.length > 0 && (
              <p className="summary-ship-note">
                <span className="summary-ship-icon" aria-hidden="true">🚚</span>
                <span><strong>Avakaaya.com Delivery</strong>{deliveryEstimate ? <> · {deliveryEstimate}</> : null}</span>
              </p>
            )}
            {!liveRatesLoading && liveRates && liveRates.length === 0 && (
              <p className="summary-ship-note summary-ship-note--warn">
                <span className="summary-ship-icon" aria-hidden="true">⚠️</span>
                <span>Could not fetch rates for this pincode</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {upiPayment && (
        <div className="upi-modal-overlay" role="dialog" aria-modal="true">
          <div className="upi-modal">
            <div className="upi-modal-header">
              <h2>Pay via UPI</h2>
              <button
                className="upi-modal-close"
                onClick={() => setUpiPayment(null)}
                aria-label="Close"
              >✕</button>
            </div>

            <div className="upi-modal-body">
              <p className="upi-modal-amount">
                Amount: <strong>₹{upiPayment.amount}</strong>
              </p>
              <p className="upi-modal-ref">
                Reference: <code>{upiPayment.reference}</code>
              </p>

              <div className="upi-qr-wrap">
                <img src={upiPayment.qrCodeUrl} alt="UPI QR" className="upi-qr" />
                <p className="upi-qr-hint">Scan with any UPI app</p>
              </div>

              <div className="upi-or">— or —</div>

              <div className="upi-deeplink-row">
                <a href={upiPayment.upiUrl} className="btn btn-primary btn-block">
                  Open in UPI app
                </a>
                <p className="upi-hint">Works on mobile. Returns you here after payment.</p>
              </div>

              <div className="upi-vpa-row">
                <div>
                  <div className="upi-vpa-label">UPI ID</div>
                  <div className="upi-vpa-value">{upiPayment.vpa}</div>
                </div>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => copyToClipboard(upiPayment.vpa, 'UPI ID')}
                >Copy</button>
              </div>

              <div className="upi-claim">
                <h3>After paying, confirm here</h3>
                <p className="upi-claim-hint">
                  Enter the transaction reference / UTR shown in your UPI app.
                  We&apos;ll verify and confirm your order shortly.
                </p>
                <input
                  type="text"
                  className="upi-input"
                  placeholder="UPI transaction reference / UTR"
                  value={upiTxnRef}
                  onChange={e => setUpiTxnRef(e.target.value)}
                />
                <input
                  type="text"
                  className="upi-input"
                  placeholder="Your UPI ID (optional)"
                  value={upiPayerVpa}
                  onChange={e => setUpiPayerVpa(e.target.value)}
                />
                <button
                  className="btn btn-primary btn-lg btn-block"
                  disabled={upiClaiming}
                  onClick={claimUpiPayment}
                >
                  {upiClaiming ? 'Submitting…' : 'I have paid'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
